// Manage page: reached via the capability URL /e/:id/manage/:token. Lets the
// owner edit the event, manage attachments, delete it, and grab the QR. Sets a
// no-referrer policy so the secret token cannot leak via the Referer header.

import {
  ApiError,
  deleteAttachment,
  deleteEvent,
  getEventDetail,
  updateEvent,
  uploadAttachment,
} from '../lib/api';
import { forgetOwned } from '../lib/ownership';
import { navigate } from '../lib/router';
import { renderEventForm } from './eventForm';
import { buildQrCard } from './qrCard';
import type { AttachmentMeta, LocalEventDetail } from '../types/event';

const MAX_TOTAL = 20 * 1024 * 1024;

function ensureNoReferrer(): void {
  if (document.querySelector('meta[name="referrer"]')) return;
  const m = document.createElement('meta');
  m.name = 'referrer';
  m.content = 'no-referrer';
  document.head.appendChild(m);
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function renderManagePage(root: HTMLElement, params: Record<string, string>): void {
  ensureNoReferrer();
  const id = params.id;
  const token = params.token;

  root.innerHTML = `<section class="page"><div class="placeholder">Loading your event...</div></section>`;

  getEventDetail(id, token)
    .then((ev) => renderLoaded(root, ev, id, token))
    .catch((err) => {
      if (err instanceof ApiError && err.status === 404) forgetOwned(id);
      const msg =
        err instanceof ApiError && (err.status === 404 || err.status === 403)
          ? 'This manage link is invalid or the event no longer exists.'
          : 'Could not load this event.';
      root.innerHTML = `<section class="page"><a class="back-link" href="/" data-link>&larr; Back to events</a><div class="placeholder error">${esc(msg)}</div></section>`;
    });
}

function renderLoaded(
  root: HTMLElement,
  ev: LocalEventDetail,
  id: string,
  token: string
): void {
  root.innerHTML = `
    <section class="page manage-page">
      <a class="back-link" href="/" data-link>&larr; Back to events</a>
      <h2>Manage event</h2>
      ${ev.status === 'removed' ? '<div class="banner warn">This event has been removed by an admin and is hidden from the public.</div>' : ''}
      <p class="page-lead">You are editing as the owner. Keep your manage link private.</p>
      <div id="form-mount"></div>
      <p class="form-error" id="manage-error" role="alert"></p>

      <hr class="divider" />
      <h3>Attachments</h3>
      <p class="field-hint">Images and PDFs. Up to 20MB total, 10 files. <span id="usage"></span></p>
      <div id="attach-list" class="manage-attach-list"></div>
      <div class="upload-row">
        <input id="file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" />
        <button id="upload-btn" class="btn" type="button">Upload</button>
        <span id="upload-status" class="field-hint"></span>
      </div>

      <hr class="divider" />
      <h3>Share</h3>
      <div id="qr-mount" class="qr-mount"></div>

      <hr class="divider" />
      <h3 class="danger-title">Danger zone</h3>
      <button id="delete-btn" class="btn danger" type="button">Delete this event</button>
    </section>
  `;

  // Edit form.
  const formMount = root.querySelector<HTMLElement>('#form-mount')!;
  const form = renderEventForm(
    {
      name: ev.title,
      category: ev.category,
      age21Plus: ev.age21Plus,
      description: ev.description,
      address: ev.address,
      locationName: ev.venueName,
      start: ev.start,
      end: ev.end,
    },
    { submitLabel: 'Save changes' }
  );
  formMount.appendChild(form.element);
  const errEl = root.querySelector<HTMLElement>('#manage-error')!;

  form.element.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.textContent = '';
    const { value, errors } = form.read();
    if (!value) {
      form.showErrors(errors);
      return;
    }
    form.setBusy(true);
    try {
      await updateEvent(id, value, { editToken: token });
      form.setBusy(false);
      errEl.textContent = 'Saved.';
      errEl.classList.add('ok');
      setTimeout(() => {
        errEl.textContent = '';
        errEl.classList.remove('ok');
      }, 2000);
    } catch (err) {
      form.setBusy(false);
      if (err instanceof ApiError) {
        if (err.fields) form.showErrors(err.fields);
        errEl.textContent = err.message;
      } else {
        errEl.textContent = 'Could not save. Please try again.';
      }
    }
  });

  // QR.
  root.querySelector<HTMLElement>('#qr-mount')!.appendChild(
    buildQrCard({ name: ev.title, start: ev.start, publicUrl: ev.sourceUrl })
  );

  // Attachments state + rendering.
  let attachments: AttachmentMeta[] = [...(ev.attachments ?? [])];
  const listEl = root.querySelector<HTMLElement>('#attach-list')!;
  const usageEl = root.querySelector<HTMLElement>('#usage')!;

  function renderAttachments(): void {
    listEl.innerHTML = '';
    for (const a of attachments) {
      const row = document.createElement('div');
      row.className = 'attach-row';
      const label = document.createElement('span');
      label.textContent = `${a.kind === 'image' ? '\u{1F5BC}' : '\u{1F4C4}'} ${a.filename} (${fmtBytes(a.sizeBytes)})`;
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'btn small danger';
      del.textContent = 'Remove';
      del.addEventListener('click', async () => {
        del.disabled = true;
        try {
          await deleteAttachment(id, a.id, token);
          attachments = attachments.filter((x) => x.id !== a.id);
          renderAttachments();
        } catch {
          del.disabled = false;
        }
      });
      row.append(label, del);
      listEl.appendChild(row);
    }
    const used = attachments.reduce((s, a) => s + a.sizeBytes, 0);
    usageEl.textContent = `Using ${fmtBytes(used)} of 20MB (${attachments.length}/10).`;
  }
  renderAttachments();

  // Upload.
  const fileInput = root.querySelector<HTMLInputElement>('#file-input')!;
  const uploadBtn = root.querySelector<HTMLButtonElement>('#upload-btn')!;
  const uploadStatus = root.querySelector<HTMLElement>('#upload-status')!;
  uploadBtn.addEventListener('click', async () => {
    const file = fileInput.files?.[0];
    if (!file) {
      uploadStatus.textContent = 'Choose a file first.';
      return;
    }
    const used = attachments.reduce((s, a) => s + a.sizeBytes, 0);
    if (used + file.size > MAX_TOTAL) {
      uploadStatus.textContent = 'That would exceed the 20MB limit.';
      return;
    }
    uploadBtn.disabled = true;
    uploadStatus.textContent = 'Uploading...';
    try {
      const meta = await uploadAttachment(id, file, token);
      attachments.push(meta);
      renderAttachments();
      fileInput.value = '';
      uploadStatus.textContent = 'Uploaded.';
    } catch (err) {
      uploadStatus.textContent = err instanceof ApiError ? err.message : 'Upload failed.';
    } finally {
      uploadBtn.disabled = false;
    }
  });

  // Delete event.
  const deleteBtn = root.querySelector<HTMLButtonElement>('#delete-btn')!;
  deleteBtn.addEventListener('click', async () => {
    if (!confirm('Delete this event permanently? This cannot be undone.')) return;
    deleteBtn.disabled = true;
    try {
      await deleteEvent(id, { editToken: token });
      forgetOwned(id);
      navigate('/');
    } catch (err) {
      deleteBtn.disabled = false;
      errEl.textContent = err instanceof ApiError ? err.message : 'Could not delete.';
    }
  });
}
